"use client";

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";

import {
  Bars3Icon,
  XMarkIcon,
  ChatBubbleBottomCenterIcon,
  ArrowUpOnSquareStackIcon,
  SparklesIcon,
  UserIcon,
  InboxIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ArrowLeftStartOnRectangleIcon,
} from "@heroicons/react/24/outline";

const email = "johndoe@example.com";

const navigation = [
  {
    name: "Blog To Posts",
    href: "/createposts",
    icon: ArrowUpOnSquareStackIcon,
  },
  {
    name: "Generated Tweets",
    href: "/savedposts",
    icon: ChatBubbleBottomCenterIcon,
  },
  // {
  //   name: "Profile",
  //   href: "/profile",
  //   icon: UserIcon,
  // },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Sidebar({ collapsed, toggleCollapse }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const localtoken = localStorage.getItem("token");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
    navigate(0);
  };

  return (
    <>
      {/* ---------------- MOBILE ---------------- */}
      <Dialog
        open={sidebarOpen}
        onClose={setSidebarOpen}
        className="lg:hidden relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/50" />

        <div className="fixed inset-0 flex">
          <DialogPanel className="w-64 bg-white p-4">
            <button onClick={() => setSidebarOpen(false)}>
              <XMarkIcon className="w-6" />
            </button>

            <div className="mt-6 flex flex-col gap-4">
              {navigation.map((item) => (
                <a key={item.name} href={item.href} className="flex gap-3">
                  <item.icon className="w-5" />
                  {item.name}
                </a>
              ))}
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* ---------------- DESKTOP ---------------- */}
      <div
        className={classNames(
          "hidden lg:flex flex-col fixed left-4 top-4 bottom-4 z-50 gap-4 transition-all duration-300",
          collapsed ? "w-20" : "w-64",
        )}
      >
        <div className="flex flex-col h-full gap-4">
          {/* ---------------- TOP CARD ---------------- */}
          <div className="bg-white/90 backdrop-blur rounded-2xl shadow border p-3">
            {/* Toggle */}
            <div className="flex justify-end mb-2">
              <button onClick={() => toggleCollapse(!collapsed)}>
                {collapsed ? (
                  <ChevronRightIcon className="w-5 text-gray-500" />
                ) : (
                  <ChevronLeftIcon className="w-5 text-gray-500" />
                )}
              </button>
            </div>

            {/* Logo */}
            <div
              className={classNames(
                "mb-4 font-bold text-lg flex items-center h-10 relative group",
                collapsed ? "pl-2 justify-start" : "px-2",
              )}
            >
              {collapsed ? "B" : "BlogSlicer"}

              {/* Tooltip */}
              {collapsed && (
                <span className="absolute left-14 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                  BlogSlicer
                </span>
              )}
            </div>

            {/* NAV */}
            <div className="flex flex-col gap-2">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="group flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition relative"
                >
                  <item.icon className="w-5 h-5 text-gray-600" />

                  {!collapsed && (
                    <span className="text-sm text-gray-800">{item.name}</span>
                  )}

                  {/* Tooltip */}
                  {collapsed && (
                    <span className="absolute left-16 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100">
                      {item.name}
                    </span>
                  )}
                </a>
              ))}
            </div>
          </div>

          {/* ---------------- BOTTOM CARD ---------------- */}
          <div className="mt-auto bg-white/90 backdrop-blur rounded-2xl shadow border p-3">
            {/* Support */}
            <a
              href="/support"
              className="group flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition"
            >
              <InboxIcon className="w-5 h-5 text-gray-600" />
              {!collapsed && <span className="text-sm">Support</span>}
              {/* Tooltip */}
              {collapsed && (
                <span className="absolute left-16 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100">
                  Support
                </span>
              )}
            </a>

            {/* Divider */}
            <div className="my-2 border-t" />
            <div className="mb-3 px-2">
              {!collapsed && (
                <div className="text-xs text-gray-500">{email}</div>
              )}
            </div>

            {/* Profile */}
            <button
              onClick={() => navigate("/profile")}
              className="group w-full text-left flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition"
            >
              <UserIcon className="w-5 h-5 text-gray-600" />
              {!collapsed && <span className="text-sm">Profile</span>}

              {/* Tooltip */}
              {collapsed && (
                <span className="absolute left-16 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100">
                  Profile
                </span>
              )}
            </button>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="group w-full text-left flex items-center gap-3 p-2 rounded-lg hover:bg-red-50 transition"
            >
              <ArrowLeftStartOnRectangleIcon className="w-5 h-5 text-red-500" />
              {!collapsed && (
                <span className="text-sm text-red-600">Logout</span>
              )}
              {/* Tooltip */}
              {collapsed && (
                <span className="absolute left-16 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100">
                  Logout
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
